import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { FilterState } from '@/types/filterTypes';

export interface FilterPreset {
  id: string;
  name: string;
  filters: FilterState;
  company_id: string | null;
  created_by: string | null;
  is_shared: boolean;
  created_at: string;
}

export function useFilterPresets() {
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentCompany } = useCompany();
  const { user } = useAuth();

  const fetchPresets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      let query = (supabase
        .from('filter_presets' as any)
        .select('*')
        .order('created_at', { ascending: false })) as any;

      if (currentCompany?.company_id) {
        query = query.eq('company_id', currentCompany.company_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setPresets((data || []) as unknown as FilterPreset[]);
    } catch (err: any) {
      console.error('Error fetching filter presets:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentCompany?.company_id]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const savePreset = async (name: string, filters: FilterState) => {
    if (!user) return;
    try {
      const { data, error } = await (supabase
        .from('filter_presets' as any)
        .insert({
          name,
          filters: filters as any,
          company_id: currentCompany?.company_id || null,
          created_by: user.id,
          is_shared: false,
        }) as any)
        .select()
        .single();

      if (error) throw error;
      setPresets(prev => [data as unknown as FilterPreset, ...prev]);
      toast.success(`Preset "${name}" saved`);
    } catch (err: any) {
      console.error('Error saving preset:', err);
      toast.error('Failed to save preset');
    }
  };

  const deletePreset = async (id: string) => {
    try {
      const { error } = await (supabase
        .from('filter_presets' as any)
        .delete() as any)
        .eq('id', id);

      if (error) throw error;
      setPresets(prev => prev.filter(p => p.id !== id));
      toast.success('Preset deleted');
    } catch (err: any) {
      console.error('Error deleting preset:', err);
      toast.error('Failed to delete preset');
    }
  };

  return { presets, loading, savePreset, deletePreset, refetch: fetchPresets };
}
